import Svg, { Circle, Line, Path, Rect } from 'react-native-svg';
import { colors } from '../theme';

export type AppIconName =
  | 'home'
  | 'trending-up'
  | 'plus-circle'
  | 'user'
  | 'settings'
  | 'calendar'
  | 'users'
  | 'percent'
  | 'graduation-cap'
  | 'building'
  | 'user-plus'
  | 'calendar-check'
  | 'share';

type AppIconProps = {
  name: AppIconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
};

const STROKE = 1.75;

type StrokeProps = {
  stroke: string;
  strokeWidth: number;
  strokeLinecap: 'round';
  strokeLinejoin: 'round';
  fill: 'none';
};

function stroke(color: string, strokeWidth: number): StrokeProps {
  return {
    stroke: color,
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    fill: 'none',
  };
}

export function AppIcon({
  name,
  size = 24,
  color = colors.muted,
  strokeWidth = STROKE,
}: AppIconProps) {
  const s = stroke(color, strokeWidth);

  switch (name) {
    case 'home':
      return (
        <Svg width={size} height={size} viewBox="30.5 20.5 23 23" overflow="visible">
          <Path
            {...s}
            d="M39.02 22.8226L33.63 27.0226C32.73 27.7226 32 29.2126 32 30.3426V37.7526C32 40.0726 33.89 41.9726 36.21 41.9726H47.79C50.11 41.9726 52 40.0726 52 37.7626V30.4826C52 29.2726 51.19 27.7226 50.2 27.0326L44.02 22.7026C42.62 21.7226 40.37 21.7726 39.02 22.8226Z"
          />
          <Path {...s} d="M42 37.9725V34.9725" />
        </Svg>
      );
    case 'trending-up':
      return (
        <Svg width={size} height={size} viewBox="95.5 20.5 23 23" overflow="visible">
          <Path
            {...s}
            d="M104 42H110C115 42 117 40 117 35V29C117 24 115 22 110 22H104C99 22 97 24 97 29V35C97 40 99 42 104 42Z"
          />
          <Path {...s} d="M105.59 27.6799H109.83V31.9299" />
          <Path {...s} d="M109.83 27.6799L104.17 33.3399" />
          <Path {...s} d="M101 36.51C104.89 37.81 109.11 37.81 113 36.51" />
        </Svg>
      );
    case 'plus-circle':
      return (
        <Svg width={size} height={size} viewBox="160.5 20.5 23 23" overflow="visible">
          <Path
            {...s}
            d="M169 42H175C180 42 182 40 182 35V29C182 24 180 22 175 22H169C164 22 162 24 162 29V35C162 40 164 42 169 42Z"
          />
          <Path {...s} d="M170.74 35.53L174.26 32L170.74 28.47" />
        </Svg>
      );
    case 'user':
      return (
        <Svg width={size} height={size} viewBox="226.5 20.5 23 23" overflow="visible">
          <Path
            {...s}
            d="M238.12 32.78C238.05 32.77 237.96 32.77 237.88 32.78C236.12 32.72 234.72 31.28 234.72 29.51C234.72 27.7 236.18 26.23 238 26.23C239.81 26.23 241.28 27.7 241.28 29.51C241.27 31.28 239.88 32.72 238.12 32.78Z"
          />
          <Path
            {...s}
            d="M244.74 39.3801C242.96 41.0101 240.6 42.0001 238 42.0001C235.4 42.0001 233.04 41.0101 231.26 39.3801C231.36 38.4401 231.96 37.5201 233.03 36.8001C235.77 34.9801 240.25 34.9801 242.97 36.8001C244.04 37.5201 244.64 38.4401 244.74 39.3801Z"
          />
          <Path
            {...s}
            d="M238 42C243.523 42 248 37.5228 248 32C248 26.4772 243.523 22 238 22C232.477 22 228 26.4772 228 32C228 37.5228 232.477 42 238 42Z"
          />
        </Svg>
      );
    case 'settings':
      return (
        <Svg width={size} height={size} viewBox="290.5 19.5 21 22.5" overflow="visible">
          <Path
            {...s}
            d="M292 28V33.77C292 35.89 292 35.89 294 37.24L299.5 40.42C300.33 40.9 301.68 40.9 302.5 40.42L308 37.24C310 35.89 310 35.89 310 33.78V28C310 25.89 310 25.89 308 24.54L302.5 21.36C301.68 20.88 300.33 20.88 299.5 21.36L294 24.54C292 25.89 292 25.89 292 28Z"
          />
          <Path
            {...s}
            d="M301 33.89C302.657 33.89 304 32.5469 304 30.89C304 29.2332 302.657 27.89 301 27.89C299.343 27.89 298 29.2332 298 30.89C298 32.5469 299.343 33.89 301 33.89Z"
          />
        </Svg>
      );
    case 'calendar':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Rect {...s} x="3" y="4" width="18" height="18" rx="2" />
          <Line {...s} x1="16" y1="2" x2="16" y2="6" />
          <Line {...s} x1="8" y1="2" x2="8" y2="6" />
          <Line {...s} x1="3" y1="10" x2="21" y2="10" />
        </Svg>
      );
    case 'calendar-check':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Rect {...s} x="3" y="4" width="18" height="18" rx="2" />
          <Line {...s} x1="16" y1="2" x2="16" y2="6" />
          <Line {...s} x1="8" y1="2" x2="8" y2="6" />
          <Line {...s} x1="3" y1="10" x2="21" y2="10" />
          <Path {...s} d="M9 16l2 2 4-4" />
        </Svg>
      );
    case 'users':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Path {...s} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <Circle {...s} cx="9" cy="7" r="4" />
          <Path {...s} d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <Path {...s} d="M16 3.13a4 4 0 0 1 0 7.75" />
        </Svg>
      );
    case 'percent':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Line {...s} x1="19" y1="5" x2="5" y2="19" />
          <Circle {...s} cx="6.5" cy="6.5" r="2.5" />
          <Circle {...s} cx="17.5" cy="17.5" r="2.5" />
        </Svg>
      );
    case 'graduation-cap':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Path {...s} d="M22 10v6M2 10l10-5 10 5-10 5z" />
          <Path {...s} d="M6 12v5c0 1.1 2.7 2 6 2s6-.9 6-2v-5" />
        </Svg>
      );
    case 'building':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Rect {...s} x="4" y="2" width="16" height="20" rx="2" />
          <Path {...s} d="M9 22v-4h6v4" />
          <Path {...s} d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01M12 14h.01" />
        </Svg>
      );
    case 'user-plus':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Path {...s} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <Circle {...s} cx="9" cy="7" r="4" />
          <Line {...s} x1="19" y1="8" x2="19" y2="14" />
          <Line {...s} x1="16" y1="11" x2="22" y2="11" />
        </Svg>
      );
    case 'share':
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Path {...s} d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
          <Path {...s} d="M16 6l-4-4-4 4" />
          <Path {...s} d="M12 2v13" />
        </Svg>
      );
    default:
      return null;
  }
}
